/**
 * Task Configuration
 * Status, priority, and phase display configurations following the Medley Design System
 */

import type { TaskStatus, TaskPriority, TaskPhase, TaskAnchorType } from '../types';

export interface StatusConfig {
  label: string;
  color: string;
  variant: 'filled' | 'light' | 'outline';
}

export interface PriorityConfig {
  label: string;
  color: string;
  icon?: string;
}

export interface PhaseConfig {
  label: string;
  shortLabel: string;
  description: string;
  color: string;
}

/**
 * Task status display configuration
 * MED-161: Updated colors per design spec
 */
export const TASK_STATUS_CONFIG: Record<TaskStatus, StatusConfig> = {
  not_started: {
    label: 'Not Started',
    color: 'gray',
    variant: 'light',
  },
  in_progress: {
    label: 'In Progress',
    color: 'taskInProgress', // MED-161: #D1D69B bg, #28463A text
    variant: 'filled',
  },
  complete: {
    label: 'Complete',
    color: 'medleyGreen', // MED-161: #28463A bg, #FFFFFF text
    variant: 'filled',
  },
  blocked: {
    label: 'Blocked',
    color: 'dangerRed',
    variant: 'light',
  },
  cancelled: {
    label: 'Cancelled',
    color: 'statusCancelled', // MED-161: #888888 bg
    variant: 'filled',
  },
};

/**
 * Task priority display configuration
 * MED-161: Updated colors per design spec
 */
export const TASK_PRIORITY_CONFIG: Record<TaskPriority, PriorityConfig> = {
  low: {
    label: 'Low',
    color: 'gray',
  },
  medium: {
    label: 'Medium',
    color: 'statusLive', // MED-161: #F09A65 bg, #FFFFFF text
  },
  high: {
    label: 'High',
    color: 'importanceHigh', // MED-161: #E53935 bg, #FFFFFF text
  },
  urgent: {
    label: 'Urgent',
    color: 'dangerRed',
  },
};

/**
 * Task phase display configuration
 */
export const TASK_PHASE_CONFIG: Record<TaskPhase, PhaseConfig> = {
  cohort_setup: {
    label: 'Cohort Setup & Design',
    shortLabel: 'Setup',
    description: 'Initial cohort configuration and partner coordination',
    color: 'blue',
  },
  cohort_prep: {
    label: 'Cohort Preparation',
    shortLabel: 'Prep',
    description: 'Participant onboarding and materials preparation',
    color: 'grape',
  },
  live_delivery: {
    label: 'Live Cohort Delivery',
    shortLabel: 'Live',
    description: 'Active session delivery and management',
    color: 'dangerRed',
  },
  wrap_renewal: {
    label: 'Wrap & Renewal',
    shortLabel: 'Wrap',
    description: 'Program completion and renewal discussions',
    color: 'medleyGreen',
  },
};

/**
 * Human-readable phase display names
 */
export const PHASE_DISPLAY_NAMES: Record<TaskPhase, string> = {
  cohort_setup: 'Cohort Setup & Design',
  cohort_prep: 'Cohort Preparation',
  live_delivery: 'Live Cohort Delivery',
  wrap_renewal: 'Wrap & Renewal',
};

/**
 * Get status configuration
 */
export function getStatusConfig(status: TaskStatus): StatusConfig {
  return TASK_STATUS_CONFIG[status] || TASK_STATUS_CONFIG.not_started;
}

/**
 * Get priority configuration
 */
export function getPriorityConfig(priority: TaskPriority): PriorityConfig {
  return TASK_PRIORITY_CONFIG[priority] || TASK_PRIORITY_CONFIG.medium;
}

/**
 * Get phase configuration
 */
export function getPhaseConfig(phase: TaskPhase | null): PhaseConfig | null {
  if (!phase) return null;
  return TASK_PHASE_CONFIG[phase] || null;
}

/**
 * All status options for dropdowns/filters
 */
export const TASK_STATUS_OPTIONS = Object.entries(TASK_STATUS_CONFIG).map(([value, config]) => ({
  value: value as TaskStatus,
  label: config.label,
}));

/**
 * All priority options for dropdowns/filters
 */
export const TASK_PRIORITY_OPTIONS = Object.entries(TASK_PRIORITY_CONFIG).map(([value, config]) => ({
  value: value as TaskPriority,
  label: config.label,
}));

/**
 * All phase options for dropdowns/filters
 */
export const TASK_PHASE_OPTIONS = Object.entries(TASK_PHASE_CONFIG).map(([value, config]) => ({
  value: value as TaskPhase,
  label: config.label,
}));

/**
 * Phase order for sorting/display
 */
export const PHASE_ORDER: TaskPhase[] = [
  'cohort_setup',
  'cohort_prep',
  'live_delivery',
  'wrap_renewal',
];

// ============================================================================
// Phase Status Configuration (for accordion headers)
// ============================================================================

export type PhaseStatus = 'not_started' | 'in_progress' | 'complete';

export interface PhaseStatusConfig {
  label: string;
  color: string;
  variant: 'filled' | 'light' | 'outline';
}

/**
 * Phase status display configuration for accordion headers
 */
export const PHASE_STATUS_CONFIG: Record<PhaseStatus, PhaseStatusConfig> = {
  complete: {
    label: 'COMPLETE',
    color: 'medleyGreen',
    variant: 'filled',
  },
  in_progress: {
    label: 'IN PROGRESS',
    color: 'taskInProgress',
    variant: 'filled',
  },
  not_started: {
    label: 'NOT STARTED',
    color: 'gray',
    variant: 'light',
  },
};

/**
 * Get phase status configuration
 */
export function getPhaseStatusConfig(status: PhaseStatus): PhaseStatusConfig {
  return PHASE_STATUS_CONFIG[status] || PHASE_STATUS_CONFIG.not_started;
}

/**
 * Calculate phase status based on tasks within the phase
 */
export function calculatePhaseStatus(tasks: { status: string }[]): PhaseStatus {
  if (tasks.length === 0) return 'not_started';

  const completedCount = tasks.filter(t => t.status === 'complete').length;

  if (completedCount === tasks.length) return 'complete';
  if (completedCount > 0 || tasks.some(t => t.status === 'in_progress')) return 'in_progress';

  return 'not_started';
}

/**
 * Anchor type configuration for templates
 */
export interface AnchorConfig {
  label: string;
  description: string;
}

export const TASK_ANCHOR_CONFIG: Record<TaskAnchorType, AnchorConfig> = {
  cohort_start: {
    label: 'Cohort Start',
    description: 'Days relative to cohort start date',
  },
  cohort_end: {
    label: 'Cohort End',
    description: 'Days relative to cohort end date',
  },
  orientation: {
    label: 'Orientation',
    description: 'Days relative to orientation/first session',
  },
  coach_kickoff: {
    label: 'Coach Kickoff',
    description: 'Days relative to coach kickoff (first session)',
  },
  session: {
    label: 'Session',
    description: 'Days relative to a specific session',
  },
  final_session: {
    label: 'Final Session',
    description: 'Days relative to the last session',
  },
  midpoint: {
    label: 'Midpoint',
    description: 'Days relative to the midpoint session',
  },
  endpoint: {
    label: 'Endpoint',
    description: 'Days relative to cohort completion (last session)',
  },
};

/**
 * All anchor type options for dropdowns
 */
export const TASK_ANCHOR_OPTIONS = Object.entries(TASK_ANCHOR_CONFIG).map(([value, config]) => ({
  value: value as TaskAnchorType,
  label: config.label,
}));

/**
 * Get anchor type configuration
 */
export function getAnchorConfig(anchorType: TaskAnchorType): AnchorConfig {
  return TASK_ANCHOR_CONFIG[anchorType] || TASK_ANCHOR_CONFIG.cohort_start;
}
